import { cosignErrorCode } from '@constants'
import { AnalyticsPlatforms, useAnalytics } from '@dni-analysis'
import { LOGIN_TYPE } from '@dni-analysis/types/cosign-events'
import { useBeforeunload } from '@dni-react-hooks/use-beforeunload'
import { useApiWordpressPasswordExist } from '@hooks-api/cosign/use-api-wordpress-password-exist'
import { Alert } from '@pets-ui/Alert/Alert'
import { Button } from '@pets-ui/Button/Button'
import { ArrowLeftSmall } from '@pets-ui/icons'
import { Text } from '@pets-ui/Text/Text'
import * as stylex from '@stylexjs/stylex'
import { type AxiosError } from 'axios'
import { format } from 'date-fns'
import queryString from 'query-string'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOtpSendErrorHandler } from '../../../hooks/use-otp-send-error-handler'
import { useEffectOnce } from '../../../hooks/useEffectOnce'
import { useLineInvitationCheck } from '../../../hooks/useLineInvitationCheck'
import { useOtpCosignSend } from '../../../hooks/useOtpCosignSend'
import { useTimer } from '../../../hooks/useTimer'
import { type SendOtpErrorResponse } from '../../../types/apiErrorResponse'
import { getAccount } from '../../../utils/cookie'
import { LoadingProvider } from '../../LoadingProvider'
import { useContextCosign } from '../../useContextCosign'
import { LoginLink } from '../LoginLink'
import { Service } from '../Service'
import { CardTitleWithIcon } from './CardTitleWithIcon'
import { DividerOr } from './DividerOr'
import { InputVerification } from './InputVerification'
import { LoginStep } from './types'
import { useContextLogin } from './useContextLogin'

const styles = stylex.create({
  subTitleContainer: {
    gap: '4px',
    alignItems: 'center',
    color: '#4B4B4B',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginBottom: '20px',
    marginTop: '20px',
  },
  validateCodeContainer: {
    gap: '8px',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '20px',
  },
  resendContainer: {
    gap: '4px',
    display: 'flex',
  },
  validateCodeTitle: {
    color: '#4B4B4B',
  },
  validateResendText: {
    color: '#AAAAAA',
  },
  buttonBack: {
    margin: '0 auto',
    color: '#7A7A7A',
    display: 'block',
    marginTop: '12px',
  },
})

export function ValidationEmail() {
  const { t } = useTranslation()
  const { setStep, email, backStep, setIsEmailDomainRejected } = useContextLogin()
  const { environment, tokenLoginUrl, redirectUrl, fallbackUrl, lineInvitation } = useContextCosign()
  const { startTimer, seconds, isCounting } = useTimer()
  const [remainingTimes, setRemainingTimes] = useState(0)
  const [inputVerificationError, setInputVerificationError] = useState('')
  const { loginSendOtp, isMutating } = useOtpCosignSend({ tokenLoginUrl, redirectUrl, fallbackUrl })
  const { sendEvent } = useAnalytics({ defaultPlatforms: [AnalyticsPlatforms.GA4] })
  const { destructuredData, isLoading: isLoginHasPassword } = useApiWordpressPasswordExist(email ? { account: email } : undefined)
  const hasPassword = destructuredData?.success
  const search = queryString.parse(window.location.search)
  const account = email || getAccount()
  const canSendOtp = useRef(!search?.otpCode)

  /** 檢查是否需要顯示 LINE 綁定邀請頁面 */
  const { isChecking: isLineStatusChecking } = useLineInvitationCheck({
    account,
    enabled: lineInvitation,
  })

  const handleOtpSendError = useOtpSendErrorHandler(useCallback((detail) => {
    startTimer(detail.expiredAt)
    setRemainingTimes(detail.remainingTimes ?? 0)
  }, [startTimer]))

  const handleSendOtp = useCallback(async () => {
    if (lineInvitation) {
      if (isLineStatusChecking) return
    }
    try {
      canSendOtp.current = false
      const { data } = await loginSendOtp('email')
      startTimer(data.expiredAt)
      setRemainingTimes(data.remainingTimes)
      if (data.remainingTimes < 5) sendEvent({ eventName: 'Email OTP resend', eventParams: { event_category: 'Email OTP', email_otp_resend_clicks: 5 - data.remainingTimes } })
    }
    catch (error) {
      const errorCode = (error as AxiosError<SendOtpErrorResponse>).response?.data?.code
      /* 網域被拒收時退回輸入頁就地標紅，別讓使用者留在這裡等一封不會寄出的信 */
      if (errorCode === cosignErrorCode.EMAIL_PROVIDER_NOT_SUPPORTED || errorCode === cosignErrorCode.EMAIL_DOMAIN_DENIED_GLOBALLY) {
        setIsEmailDomainRejected(true)
        setStep(LoginStep.LOGIN_EMAIL)
        return
      }
      handleOtpSendError(error)
    }
  }, [loginSendOtp, sendEvent, startTimer, isLineStatusChecking, lineInvitation, handleOtpSendError, setIsEmailDomainRejected, setStep])

  useEffect(() => {
    if (!canSendOtp.current) return
    handleSendOtp()
  }, [handleSendOtp])

  const handleResendOtp = useCallback(() => {
    handleSendOtp()
    setInputVerificationError('')
  }, [handleSendOtp])

  const handleClickPasswordLogin = () => {
    setStep(LoginStep.PASSWORD_LOGIN)
    sendEvent({ eventName: 'login method', eventParams: { event_category: 'Login', account_type: LOGIN_TYPE.EMAIL_PW } })
    sendEvent({ eventName: 'Email OTP pw login', eventParams: { event_category: 'Email OTP' } })
    sendEvent({ eventName: 'Email OTP drop', eventParams: { event_category: 'Email OTP' } })
  }

  const handleClickBackToLogin = () => {
    setStep(LoginStep.LOGIN)
    sendEvent({ eventName: 'Email OTP back to login', eventParams: { event_category: 'Email OTP' } })
    sendEvent({ eventName: 'Email OTP drop', eventParams: { event_category: 'Email OTP' } })
  }

  const handleClickBack = () => {
    backStep()
    sendEvent({ eventName: 'Email OTP return', eventParams: { event_category: 'Email OTP' } })
    sendEvent({ eventName: 'Email OTP drop', eventParams: { event_category: 'Email OTP' } })
  }

  useEffectOnce(() => {
    sendEvent({ eventName: 'Email OTP started', eventParams: { event_category: 'Email OTP' } })
  })

  useBeforeunload(() => {
    sendEvent({ eventName: 'Email OTP drop', eventParams: { event_category: 'Email OTP' } })
  })

  const shouldShowRemainingTimesAlert = remainingTimes != null && seconds !== 0 && (remainingTimes >= 0 && remainingTimes < 5)
  const shouldShowResendText = remainingTimes !== 0 || seconds === 0

  return (
    <LoadingProvider loading={isMutating || isLoginHasPassword}>
      <CardTitleWithIcon
        title={t('login.verifyIdentity')}
        leftIcon={<ArrowLeftSmall />}
        onClickLeft={handleClickBack}
      />
      <div {...stylex.props(styles.subTitleContainer)}>
        <Text variant="body">{t('login.sendVerifyCodeTo')}</Text>
        <Text variant="h5">{account}</Text>
      </div>
      <div {...stylex.props(styles.validateCodeContainer)}>
        <Text variant="caption" rootStyle={styles.validateCodeTitle}>{t('login.sendEmailTitle')}</Text>
        <InputVerification error={inputVerificationError} setError={setInputVerificationError} purpose="login" />
        {shouldShowResendText && (
          <div {...stylex.props(styles.resendContainer)}>
            <Text variant="caption" rootStyle={styles.validateResendText}>
              {t('login.didntReceiveVerificationCode')}
              {seconds > 0 ? `(${seconds}s)` : ''}
            </Text>
            <LoginLink disabled={isCounting} onClick={handleResendOtp}>{t('login.resend')}</LoginLink>
          </div>
        )}
        {shouldShowRemainingTimesAlert
        && (
          <Alert type="warning">
            <Text variant="caption">
              {t('login.resendErmainingTimesContent', { remainingTimes })}
              {remainingTimes === 0 && `${t('login.resendWaitContent')}(${format(seconds * 1000, 'mm:ss')})`}
            </Text>
          </Alert>
        )}
      </div>
      <DividerOr />
      {(hasPassword || environment === 'merchant') && <Button variant="outlined" color="grey" fullWidth onClick={handleClickPasswordLogin}>{t('login.passwordLogin')}</Button> }
      <Button color="grey" variant="text" rootStyle={styles.buttonBack} onClick={handleClickBackToLogin}><Text variant="body">{t('login.returnToLogin')}</Text></Button>
      <Service />
    </LoadingProvider>
  )
}
